<template>
  <div class="container max-w-3xl mx-auto px-4 py-8">
    <h1 class="text-3xl font-bold text-center mb-8 text-gray-800">Регистрация через биометрическую верификацию</h1>

    <div v-if="currentStep === 0" class="bg-white shadow-md rounded-lg p-6 mb-6">
      <h2 class="text-xl font-semibold mb-6 text-center">Предварительная информация</h2>
      
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">ФИО</label>
          <input 
            type="text" 
            v-model="userInfo.full_name"
            placeholder="Введите ваше полное имя"
            class="form-input"
            :class="{'border-red-500': errors.full_name}"
            @input="clearError('full_name')"
          >
          <p v-if="errors.full_name" class="mt-1 text-sm text-red-600">{{ errors.full_name }}</p>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">ИИН</label>
          <input 
            type="text" 
            v-model="userInfo.iin"
            placeholder="12-значный ИИН"
            class="form-input"
            maxlength="12"
            :class="{'border-red-500': errors.iin}"
            @input="clearError('iin')"
          >
          <p v-if="errors.iin" class="mt-1 text-sm text-red-600">{{ errors.iin }}</p>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Номер телефона</label>
          <input 
            type="tel" 
            v-model="userInfo.phone"
            placeholder="+7 (___) ___-__-__"
            class="form-input"
            :class="{'border-red-500': errors.phone}"
            @input="clearError('phone')"
          >
          <p v-if="errors.phone" class="mt-1 text-sm text-red-600">{{ errors.phone }}</p>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input 
            type="email" 
            v-model="userInfo.email"
            placeholder="example@domain.com"
            class="form-input"
            :class="{'border-red-500': errors.email}"
            @input="clearError('email')"
          >
          <p v-if="errors.email" class="mt-1 text-sm text-red-600">{{ errors.email }}</p>
        </div>
      </div>
      
      <div class="flex justify-center mt-6">
        <button 
          @click="startBiometricVerification" 
          class="px-6 py-3 bg-primary text-white rounded-md hover:bg-blue-600"
          :disabled="!isFormValid"
        >
          Продолжить к верификации
        </button>
      </div>
      
      <div v-if="formError" class="mt-4 bg-red-50 p-4 rounded-md text-red-700">
        {{ formError }}
      </div>
    </div>

    <div v-else-if="currentStep === 1" class="bg-white shadow-md rounded-lg p-6 mb-6">
      <BiometricVerification 
        :userInfo="userInfo"
        @verification-completed="handleVerificationCompleted"
        @continue-registration="finalizeRegistration"
        @registration-completed="handleRegistrationCompleted"
      />
    </div>
    
    <div v-else-if="currentStep === 2" class="bg-white shadow-md rounded-lg p-6 mb-6">
      <div v-if="registrationSuccess" class="success-wrapper">
        <div class="bg-green-50 p-4 rounded-md mb-6">
          <p class="text-green-700 font-medium">Регистрация через биометрическую верификацию успешно завершена!</p>
        </div>
        
        <h3 class="font-semibold text-lg mb-3">Данные аутентификации:</h3>
        <div class="bg-gray-50 p-4 rounded-md mb-4">
          <div class="mb-2">
            <span class="font-medium">Токен:</span> 
            <span class="font-mono bg-gray-200 px-2 py-1 rounded">{{ registrationData.access_token }}</span>
          </div>
          <div>
            <span class="font-medium">Пользователь:</span> 
            {{ registrationData.user?.first_name }} {{ registrationData.user?.last_name }} ({{ registrationData.user?.iin }})
          </div>
        </div>
        
        <p class="mb-4 text-gray-600">
          Этот токен можно использовать для аутентификации в API. Вы можете сохранить его для использования в мобильном приложении.
        </p>
        
        <div class="mt-6 flex justify-center">
          <router-link to="/login" class="px-6 py-3 bg-primary text-white rounded-md hover:bg-blue-600">
            Перейти к входу
          </router-link>
        </div>
      </div>
      
      <div v-else class="error-wrapper">
        <div class="bg-red-50 p-4 rounded-md mb-6">
          <p class="text-red-700 font-medium">Ошибка при регистрации: {{ registrationError }}</p>
        </div>
        
        <button @click="retry" class="px-6 py-3 bg-primary text-white rounded-md hover:bg-blue-600">
          Попробовать снова
        </button>
      </div>
    </div>
    
    <div class="mt-6 text-center">
      <router-link to="/register" class="text-primary hover:underline">
        Вернуться к стандартной регистрации
      </router-link>
    </div>
  </div>
</template>

<script>
import BiometricVerification from '@/components/BiometricVerification.vue'

export default {
  name: 'BiometricRegister',
  components: {
    BiometricVerification
  },
  data() {
    return {
      currentStep: 0,
      userInfo: {
        full_name: '',
        iin: '',
        phone: '',
        email: ''
      },
      errors: {
        full_name: '',
        iin: '',
        phone: '',
        email: ''
      },
      formError: '',
      verificationResults: null,
      registrationSuccess: false,
      registrationError: '',
      registrationData: null
    }
  },
  computed: {
    isFormValid() {
      return (
        this.userInfo.full_name.trim() !== '' && 
        this.userInfo.iin.length === 12 &&
        this.userInfo.phone.trim() !== '' &&
        this.validateEmail(this.userInfo.email)
      )
    }
  },
  methods: {
    validateEmail(email) {
      const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(String(email).toLowerCase());
    },
    
    validateForm() {
      let isValid = true;
      this.errors = {
        full_name: '',
        iin: '',
        phone: '',
        email: ''
      };
      
      if (!this.userInfo.full_name.trim()) {
        this.errors.full_name = 'Введите ваше полное имя';
        isValid = false;
      }
      
      if (!this.userInfo.iin.trim()) {
        this.errors.iin = 'Введите ваш ИИН';
        isValid = false;
      } else if (this.userInfo.iin.length !== 12) {
        this.errors.iin = 'ИИН должен содержать 12 цифр';
        isValid = false;
      }
      
      if (!this.userInfo.phone.trim()) {
        this.errors.phone = 'Введите номер телефона';
        isValid = false;
      }
      
      if (!this.userInfo.email.trim()) {
        this.errors.email = 'Введите email адрес';
        isValid = false;
      } else if (!this.validateEmail(this.userInfo.email)) {
        this.errors.email = 'Введите корректный email адрес';
        isValid = false;
      }
      
      return isValid;
    },
    
    clearError(field) {
      this.errors[field] = '';
      this.formError = '';
    },
    
    startBiometricVerification() {
      if (this.validateForm()) {
        this.currentStep = 1;
      }
    },
    
    handleVerificationCompleted(result) {
      this.verificationResults = result;
      
      // Если верификация успешна, анализируем данные
      if (result.success && result.data) {
        // Получаем сырые данные из ответа API
        const rawData = result.data;
        
        // Проверяем наличие docOwner в ответе
        if (rawData.docOwner) {
          // Извлекаем ИИН, если он есть
          if (rawData.docOwner.iin) {
            this.userInfo.iin = rawData.docOwner.iin;
          }
          
          // Извлекаем ФИО
          const lastName = rawData.docOwner.lastName || '';
          const firstName = rawData.docOwner.firstName || '';
          const middleName = rawData.docOwner.middleName || '';
          
          if (lastName || firstName || middleName) {
            this.userInfo.full_name = [lastName, firstName, middleName].filter(Boolean).join(' ');
          }
        }
        
        // Извлекаем телефон из корневых данных
        if (rawData.phone) {
          this.userInfo.phone = rawData.phone;
        }
        
        // Проверяем наличие данных в edocument_result
        if (rawData.edocument_result) {
          const edoc = rawData.edocument_result;
          
          // Извлекаем телефон из edocument_result, если он не найден ранее
          if (!this.userInfo.phone && edoc.phone) {
            this.userInfo.phone = edoc.phone;
          }
          
          // Проверяем наличие дополнительных данных в extra
          if (edoc.extra) {
            // Если данные о дате рождения помогают в идентификации, используем их
          }
          
          // Проверяем result_json на наличие дополнительных данных
          if (edoc.result_json) {
            // Извлекаем данные из domain
            if (edoc.result_json.domain) {
              const domain = edoc.result_json.domain;
              
              // Проверяем наличие ИИН
              if (!this.userInfo.iin && domain.iin) {
                this.userInfo.iin = domain.iin;
              }
            }
          }
        }
      }
    },
    
    finalizeRegistration(verificationData) {
      
      // Here we would normally process the registration
      // But for now we're just displaying the data
      
      // Optionally redirect to a success page or login
      // this.$router.push('/login');
    },
    
    handleRegistrationCompleted(result) {
      
      if (result.success) {
        this.registrationSuccess = true;
        this.registrationData = result.data;
        this.currentStep = 2;
      } else {
        this.registrationSuccess = false;
        this.registrationError = result.error || 'Неизвестная ошибка при регистрации';
        this.currentStep = 2;
      }
    },
    
    retry() {
      this.currentStep = 0;
      this.registrationSuccess = false;
      this.registrationError = '';
      this.registrationData = null;
      this.verificationResults = null;
      
      // Очищаем sessionId чтобы начать верификацию заново
      localStorage.removeItem('biometricSessionId');
    }
  },
  mounted() {
    // Check if coming back from verification
    const sessionId = localStorage.getItem('biometricSessionId');
    if (sessionId) {
      this.currentStep = 1;
    } else {
      // No action needed
    }
  },
  created() {
  }
}
</script>

<style scoped>
.form-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
}

.form-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.5);
}
</style> 